import React, { createContext, useContext, useState, useEffect } from 'react';
import type { UserProfile, ConfirmPasswordResetRequest } from '@/proto/gen/guardianai/v1/auth/auth_pb';

type GrpcServicesModule = typeof import('../api/grpc/services');
let grpcServicesPromise: Promise<GrpcServicesModule> | null = null;

const getGrpcServices = async (): Promise<GrpcServicesModule> => {
  if (!grpcServicesPromise) {
    grpcServicesPromise = import('../api/grpc/services');
  }
  return grpcServicesPromise;
};

/**
 * Authentication Hook
 * 
 * This hook provides authentication functionality using the gRPC backend.
 * NO dummy users or fallback logins - all authentication must go through the real backend.
 * 
 * Features:
 * - Real backend authentication via gRPC
 * - Session management and refresh
 * - Social login (Google, GitHub)
 * - Two-factor authentication
 * - Password reset flow
 */

// User type from protobuf
type User = any; // Use any to bridge UserProfile types across different packages

interface Session {
  id: string;
  device: string;
  location: string;
  lastActive: string;
  current: boolean;
}

interface AuthContextType {
  user: User | null;
  realUser: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithTwoFactor: (code: string) => Promise<void>;
  loginWithBackupCode: (code: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithGitHub: () => Promise<void>;
  logout: () => void;
  register: (name: string, email: string, password: string, role: string, country?: string, stateProvince?: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  enableTwoFactor: () => Promise<void>;
  disableTwoFactor: () => Promise<void>;
  generateBackupCodes: () => Promise<string[]>;
  // Session management
  getSessions: () => Promise<Session[]>;
  deleteSession: (sessionId: string) => Promise<void>;
  deleteAllSessions: () => Promise<void>;
  refreshSession: () => Promise<void>;
  // Social account management
  linkSocialAccount: (provider: 'google' | 'github') => Promise<void>;
  unlinkSocialAccount: (provider: 'google' | 'github') => Promise<void>;
  syncProfilePicture: (provider: 'google' | 'github') => Promise<void>;
  syncProfileData: (provider: 'google' | 'github') => Promise<void>;
  getSocialProfile: (provider: 'google' | 'github') => Promise<any>;
  // User management (admin/investigator)
  updateUserProfile: (update: { userId: string; email: string; fullName: string }) => Promise<UserProfile>;
  listUsers: (filter?: string) => Promise<UserProfile[]>;
  setUserRestriction: (adminId: string, userId: string, restrict: boolean, reason: string) => Promise<{ success: boolean; message: string }>;
  // OAuth integration
  setUserFromOAuth: (user: User, accessToken: string, refreshToken?: string) => void;
  // Role update (for onboarding)
  updateUserRole: (updatedUser: User) => void;
  // Impersonation
  impersonatedUser: User | null;
  isImpersonating: boolean;
  startImpersonation: (userToImpersonate: User) => void;
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionRefreshInterval, setSessionRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  // Check authentication status on mount
  const checkAuth = async () => {
    const token = localStorage.getItem('token');

    if (token) {
      try {
        const userId = localStorage.getItem('userId');
        if (!userId) throw new Error('No userId in localStorage');

        // Add a 10s timeout to the profile fetch to prevent infinite loading state
        const { userClient } = await getGrpcServices();
        const profilePromise = userClient.getUserProfile({ userId });
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Authentication check timed out')), 10000)
        );

        const profile = await Promise.race([profilePromise, timeoutPromise]) as any;
        setUser(profile);
      } catch (error) {
        // Only clear token if it's a definitive auth error, not just a timeout
        if (error instanceof Error && error.message !== 'Authentication check timed out') {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          setUser(null);
        }
      }
    } else {
      // Prevent stale impersonation UI when there is no authenticated base session.
      setImpersonatedUser(null);
      sessionStorage.removeItem('impersonatedUser');
    }
    setLoading(false);
  };

  // Call checkAuth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const startSessionRefresh = (expiresAt?: bigint) => {
    if (sessionRefreshInterval) {
      clearInterval(sessionRefreshInterval);
    }
    
    // Default to 5 minutes if no expiration provided
    let delay = 5 * 60 * 1000;
    
    if (expiresAt) {
      const expiryMs = Number(expiresAt) * 1000;
      const nowMs = Date.now();
      // Refresh 5 minutes before actual expiry, but at least 1 minute from now
      delay = Math.max(60 * 1000, (expiryMs - nowMs) - (5 * 60 * 1000));
    }

    const interval = setTimeout(refreshSession, delay);
    setSessionRefreshInterval(interval as any);
  };

  const refreshSession = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return;
      }

      const { authClient } = await getGrpcServices();
      const res = await authClient.refreshToken({ refreshToken });
      
      if (res.accessToken) {
        localStorage.setItem('token', res.accessToken);
        if (res.refreshToken) {
          localStorage.setItem('refreshToken', res.refreshToken);
        }
        if (res.accessTokenExpiresAt) {
          localStorage.setItem('tokenExp', res.accessTokenExpiresAt.toString());
        }
        
        // Schedule next refresh
        startSessionRefresh(res.accessTokenExpiresAt);
      } else {
        throw new Error('No access token received during refresh');
      }
    } catch (error) {
      if (error instanceof Error && (error.message.includes('unauthenticated') || error.message.includes('invalid'))) {
        logout();
      } else {
        // Retry in 1 minute on network errors
        const interval = setTimeout(refreshSession, 60 * 1000);
        setSessionRefreshInterval(interval as any);
      }
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const req = { email, password };
      const { authClient } = await getGrpcServices();
      const res = await authClient.login(req);
      if (!res.accessToken) throw new Error('Login failed');

      localStorage.setItem('token', res.accessToken);
      localStorage.setItem('userId', res.user?.id || '');

      // Use profile from login response if available, otherwise fetch
      if (res.user) {
        setUser(res.user);
      } else {
        const userId = localStorage.getItem('userId') || '';
        const { userClient } = await getGrpcServices();
        const userProfile = await userClient.getUserProfile({ userId });
        setUser(userProfile as any);
      }
      
      // Initial login doesn't provide expiration in this proto version, 
      // startSessionRefresh will use the fallback delay.
      startSessionRefresh();
    } catch (error: any) {
      // Import error utility at the top of the file
      const { getErrorMessage } = await import('@/utils/errorMessages');
      const friendlyMessage = getErrorMessage(error, 'login');
      throw new Error(friendlyMessage);
    }
  };

  const loginWithGoogle = async () => {
    try {
      const redirectUri = `${window.location.origin}/auth/callback/google`;
      
      // Call gRPC InitiateOAuth to get authorization URL
      const { authClient } = await getGrpcServices();
      const response = await authClient.initiateOAuth({
        provider: 'google',
        redirectUri,
      });

      if (!response.authorizationUrl) {
        throw new Error('No authorization URL received from backend');
      }

      // Preserve current URL state (like ?redirect=...) before crossing domain boundary
      sessionStorage.setItem('oauth_query_state', window.location.search);

      // Redirect to Google OAuth
      window.location.href = response.authorizationUrl;
    } catch (error: any) {
      const { getErrorMessage } = await import('@/utils/errorMessages');
      const friendlyMessage = getErrorMessage(error, 'oauth_initiation');
      throw new Error(friendlyMessage || 'Google login failed to initiate');
    }
  };

  const loginWithGitHub = async () => {
    try {
      const redirectUri = `${window.location.origin}/auth/callback/github`;
      
      // Call gRPC InitiateOAuth to get authorization URL
      const { authClient } = await getGrpcServices();
      const response = await authClient.initiateOAuth({
        provider: 'github',
        redirectUri,
      });

      if (!response.authorizationUrl) {
        throw new Error('No authorization URL received from backend');
      }

      // Preserve current URL state (like ?redirect=...) before crossing domain boundary
      sessionStorage.setItem('oauth_query_state', window.location.search);

      // Redirect to GitHub OAuth
      window.location.href = response.authorizationUrl;
    } catch (error: any) {
      const { getErrorMessage } = await import('@/utils/errorMessages');
      const friendlyMessage = getErrorMessage(error, 'oauth_initiation');
      throw new Error(friendlyMessage || 'GitHub login failed to initiate');
    }
  };


  // MFA login methods using gRPC
  const loginWithTwoFactor = async (code: string) => {
    const mfaToken = localStorage.getItem('mfaToken');
    if (!mfaToken) throw new Error('MFA token missing. Please restart login.');
    
    try {
      const { authClient } = await getGrpcServices();
      const res = await authClient.twoFactorLogin({ mfaToken, code });
      if (res.accessToken) {
        localStorage.setItem('token', res.accessToken);
        localStorage.setItem('userId', res.user?.id || '');
        if (res.refreshToken) {
          localStorage.setItem('refreshToken', res.refreshToken);
        }
        setUser(res.user);
        startSessionRefresh();
      }
    } catch (error: any) {
      const { getErrorMessage } = await import('@/utils/errorMessages');
      throw new Error(getErrorMessage(error, 'mfa_login'));
    }
  };

  const loginWithBackupCode = async (backupCode: string) => {
    const mfaToken = localStorage.getItem('mfaToken');
    if (!mfaToken) throw new Error('MFA token missing. Please restart login.');
    
    try {
      const { authClient } = await getGrpcServices();
      const res = await authClient.backupCodeLogin({ mfaToken, backupCode });
      if (res.accessToken) {
        localStorage.setItem('token', res.accessToken);
        localStorage.setItem('userId', res.user?.id || '');
        if (res.refreshToken) {
          localStorage.setItem('refreshToken', res.refreshToken);
        }
        setUser(res.user);
        startSessionRefresh();
      }
    } catch (error: any) {
      const { getErrorMessage } = await import('@/utils/errorMessages');
      throw new Error(getErrorMessage(error, 'mfa_login'));
    }
  };

  const logout = async () => {
    const token = localStorage.getItem('token');

    if (token && user) {
      try {
        const { authClient } = await getGrpcServices();
        await authClient.logout({ accessToken: token });
      } catch {
        // Continue with local logout if backend call fails.
      }
    }

    // Clear local state and storage
    setUser(null);
    setImpersonatedUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRole');
    sessionStorage.removeItem('impersonatedUser');

    // Clear session refresh interval
    if (sessionRefreshInterval) {
      clearInterval(sessionRefreshInterval);
      setSessionRefreshInterval(null);
    }
  };

  // Session management methods
  const getSessions = async () => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch('/api/auth/sessions', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch sessions');
    }

    const data = await response.json();
    return data.sessions;
  };

  const deleteSession = async (sessionId: string) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`/api/auth/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete session');
    }
  };

  const deleteAllSessions = async () => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch('/api/auth/sessions', {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete all sessions');
    }
  };

  const register = async (name: string, email: string, password: string, role: string, country: string = 'US', stateProvince: string = '') => {
    const req = { username: email, email, password, fullName: name, role, country, stateProvince };
    const { authClient } = await getGrpcServices();
    const res = await authClient.register(req);
    if (!res.userId) throw new Error('Registration failed');
    // Auto-login after registration
    await login(email, password);
  };

  const forgotPassword = async (email: string) => {
    const req = { email };
    const { authClient } = await getGrpcServices();
    await authClient.requestPasswordReset(req);
  };

  const resetPassword = async (token: string, newPassword: string) => {
    const req: ConfirmPasswordResetRequest = {
      token,
      newPassword
    } as any; // Cast if necessary while types sync
    const { authClient } = await getGrpcServices();
    await authClient.confirmPasswordReset(req as any);
  };

  const verifyEmail = async (token: string) => {
    const response = await fetch(`/api/auth/verify-email/${token}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to verify email');
    }
  };

  const enableTwoFactor = async () => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch('/api/auth/2fa/enable', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to enable 2FA');
    }
  };

  const disableTwoFactor = async () => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch('/api/auth/2fa/disable', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to disable 2FA');
    }

    setUser((prev: any) => prev ? { ...prev, isTwoFactorEnabled: false } : null);
  };

  const generateBackupCodes = async () => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch('/api/auth/2fa/backup-codes', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to generate backup codes');
    }

    return data.backupCodes;
  };

  const linkSocialAccount = async (provider: 'google' | 'github' | 'facebook' | 'twitter') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`/api/auth/link-${provider}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Failed to link ${provider} account`);
      }

      const { user: updatedUser } = await response.json();
      setUser(updatedUser);
    } catch (error) {
      throw error;
    }
  };

  const unlinkSocialAccount = async (provider: 'google' | 'github' | 'facebook' | 'twitter') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`/api/auth/unlink-${provider}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Failed to unlink ${provider} account`);
      }

      const { user: updatedUser } = await response.json();
      setUser(updatedUser);
    } catch (error) {
      throw error;
    }
  };

  const syncProfilePicture = async (provider: 'google' | 'github' | 'facebook' | 'twitter') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`/api/auth/sync-${provider}-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Failed to sync ${provider} profile picture`);
      }

      const { user: updatedUser } = await response.json();
      setUser(updatedUser);
    } catch (error) {
      throw error;
    }
  };

  const syncProfileData = async (provider: 'google' | 'github' | 'facebook' | 'twitter') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`/api/auth/sync-${provider}-profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Failed to sync ${provider} profile data`);
      }

      const { user: updatedUser } = await response.json();
      setUser(updatedUser);
    } catch (error) {
      throw error;
    }
  };

  const getSocialProfile = async (provider: 'google' | 'github' | 'facebook' | 'twitter') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`/api/auth/${provider}-profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Failed to get ${provider} profile`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  // Update user profile using gRPC
  const updateUserProfile = async (update: { userId: string; email: string; fullName: string }) => {
    const req = { userId: update.userId, email: update.email, fullName: update.fullName };
    const { userClient } = await getGrpcServices();
    const res = await userClient.getUserProfile(req);
    setUser(res as any); // Update local user state
    return res as any;
  };

  // List users (admin/investigator)
  const listUsers = async (filter: string = "") => {
    const req = { filter };
    const { userClient } = await getGrpcServices();
    const res = await userClient.listUsers(req);
    return res.users as any[];
  };

  // Restrict/enable user accounts
  const setUserRestriction = async (adminId: string, userId: string, restrict: boolean, reason: string) => {
    const req = { adminId, userId, restrict, reason };
    const { userClient } = await getGrpcServices();
    const res = await userClient.setUserRestriction(req);
    return res;
  };

  // Set user from OAuth response (called by OAuthCallback)
  const setUserFromOAuth = (user: User, accessToken: string, refreshToken?: string) => {
    // Store tokens
    localStorage.setItem('token', accessToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    localStorage.setItem('userId', user.id);

    // Set user in state
    setUser(user);

    // Start session refresh
    startSessionRefresh();
  };

  // Update user role in local state (called after role selection)
  const updateUserRole = (updatedUser: User) => {
    setUser((prev: any) => prev ? { ...prev, ...updatedUser } : updatedUser);
  };

  const startImpersonation = (userToImpersonate: User) => {
    setImpersonatedUser(userToImpersonate);
    // When impersonating, we might want to store it in session storage to persist across reloads
    sessionStorage.setItem('impersonatedUser', JSON.stringify(userToImpersonate));
  };

  const stopImpersonation = () => {
    setImpersonatedUser(null);
    sessionStorage.removeItem('impersonatedUser');
  };

  // Check for impersonation on mount
  useEffect(() => {
    const savedImpersonation = sessionStorage.getItem('impersonatedUser');
    if (savedImpersonation) {
      try {
        setImpersonatedUser(JSON.parse(savedImpersonation));
      } catch (e) {
        console.error('Failed to parse saved impersonation', e);
      }
    }
  }, []);

  const contextValue = {
    user: user ? (impersonatedUser || user) : null,
    realUser: user,
    impersonatedUser,
    isImpersonating: !!impersonatedUser,
    startImpersonation,
    stopImpersonation,
    loading,
    isAuthenticated: !!user,
    login,
    loginWithTwoFactor,
    loginWithBackupCode,
    loginWithGoogle,
    loginWithGitHub,
    logout,
    register,
    forgotPassword,
    resetPassword,
    verifyEmail,
    enableTwoFactor,
    disableTwoFactor,
    generateBackupCodes,
    getSessions,
    deleteSession,
    deleteAllSessions,
    refreshSession,
    linkSocialAccount,
    unlinkSocialAccount,
    syncProfilePicture,
    syncProfileData,
    getSocialProfile,
    updateUserProfile,
    listUsers,
    setUserRestriction,
    setUserFromOAuth,
    updateUserRole,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
